// Drywall project math: rooms → sheets, joint compound, tape, screws, corner bead.
// Sheet size: 4×8 ft = 32 sq ft. Openings deducted before waste is applied.
import { toFeet, wallAreaFromRoom, applyWaste } from '../lib/calc'
import type { UnitSystem } from '../lib/calc'
import { ceilTo, round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export interface DrywallRoom {
  name: string
  length: number
  width: number
  ceilingHeight: number
  doors: number       // each subtracts 20 sq ft
  windows: number     // each subtracts 15 sq ft
}

// 'dimensions' = user enters one or more rooms with L × W × H + openings.
// 'area' = user already knows the total net area (walls + optional ceiling,
// minus openings) and types it directly.
export type DrywallInputMode = 'dimensions' | 'area'

export interface DrywallInputs {
  unit: UnitSystem
  inputMode: DrywallInputMode
  rooms: DrywallRoom[]
  totalArea: number         // sq ft (imperial) or m² (metric) — used in 'area' mode
  needCeiling: boolean
  cornerBeadSticks: number  // 8-ft sticks of outside corner bead (user-set)
  wastePct: number          // 5-20, default 10
}

export interface DrywallResult {
  wallArea: number         // sq ft net (after deductions, before waste)
  ceilingArea: number      // sq ft net (before waste)
  totalArea: number        // sq ft net combined
  areaWithWaste: number    // sq ft combined after waste
  sheets: number           // 4×8 sheets
  compoundBuckets: number  // 4.5-gal (approx 62 lb) buckets
  tapeRolls: number        // 75-ft rolls
  screwBoxes: number       // boxes of 100 screws
  cornerBeadSticks: number // echoed from input
  lines: AccessoryLine[]
}

export const DEFAULT_DRYWALL: DrywallInputs = {
  unit: 'imperial',
  inputMode: 'dimensions',
  // Name starts empty so the "Select a room" placeholder is the dropdown's
  // default. Dimensions are neutral 12×10×9 ft until the user picks a room.
  rooms: [{ name: '', length: 12, width: 10, ceilingHeight: 9, doors: 1, windows: 1 }],
  totalArea: 361,           // 2*(12+10)*9 - 20 - 15 = 361 sq ft (matches default room)
  needCeiling: false,
  cornerBeadSticks: 0,
  wastePct: 10,
}

const SHEET_SQFT = 32          // 4 × 8 ft
const COMPOUND_SQFT = 500      // sq ft per 4.5-gal bucket
const TAPE_FT_PER_ROLL = 75
const TAPE_SQFT_RATIO = 100    // 1 roll per 100 sq ft
const SCREWS_PER_SHEET = 6     // average (field + perimeter)
const SCREWS_PER_BOX = 100

export function computeDrywall(i: DrywallInputs, locale: Locale = 'en'): DrywallResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  let wallArea = 0
  let ceilingArea = 0

  if (i.inputMode === 'area') {
    // User-entered total is already net of openings and includes everything they
    // want to drywall — we treat it as one undifferentiated area.
    wallArea = i.unit === 'metric' ? Math.max(0, i.totalArea) * 10.7639104 : Math.max(0, i.totalArea)
    ceilingArea = 0
  } else {
    for (const r of i.rooms ?? []) {
      const lFt = toFeet(r.length, i.unit)
      const wFt = toFeet(r.width, i.unit)
      const hFt = toFeet(r.ceilingHeight, i.unit)
      const roomWall = wallAreaFromRoom(wFt, lFt, hFt)
      const deductions = Math.max(0, r.doors) * 20 + Math.max(0, r.windows) * 15
      wallArea += Math.max(0, roomWall - deductions)
      if (i.needCeiling) {
        ceilingArea += Math.max(0, lFt) * Math.max(0, wFt)
      }
    }
  }

  const totalArea = wallArea + ceilingArea
  const areaWithWaste = applyWaste(totalArea, i.wastePct)

  const sheets = totalArea > 0 ? ceilTo(areaWithWaste / SHEET_SQFT, 1) : 0
  const compoundBuckets = totalArea > 0 ? Math.max(1, ceilTo(areaWithWaste / COMPOUND_SQFT, 1)) : 0
  const tapeRolls = totalArea > 0 ? Math.max(1, ceilTo(areaWithWaste / TAPE_SQFT_RATIO, 1)) : 0
  const screwsNeeded = sheets * SCREWS_PER_SHEET
  const screwBoxes = sheets > 0 ? Math.max(1, ceilTo(screwsNeeded / SCREWS_PER_BOX, 1)) : 0
  const cornerBeadSticks = Math.max(0, i.cornerBeadSticks)

  const lines: AccessoryLine[] = []

  lines.push({
    item: 'all purpose joint compound drywall mud',
    label: tr('Joint compound', 'Composé à joints'),
    kind: 'consumable',
    qty: compoundBuckets,
    unit: 'bucket',
    note: tr(`4.5-gal buckets · ~${COMPOUND_SQFT} sq ft per bucket · all-purpose for taping + finishing coats`, `Seaux de 4,5 gal · ~${COMPOUND_SQFT} pi² par seau · tout usage pour le rubanage et les couches de finition`),
  })

  lines.push({
    item: 'drywall mesh tape fiberglass',
    label: tr('Drywall mesh tape', 'Ruban à joints en treillis'),
    kind: 'consumable',
    qty: tapeRolls,
    unit: 'roll',
    note: tr(`75 ft rolls · ~1 roll per 100 sq ft · self-adhesive fiberglass mesh is easier for DIY`, `Rouleaux de 75 pi · ~1 rouleau par 100 pi² · le treillis de fibre de verre autocollant est plus facile pour le bricoleur`),
  })

  lines.push({
    item: 'drywall screws coarse thread 1-5/8',
    label: tr('Drywall screws', 'Vis à gypse'),
    kind: 'consumable',
    qty: screwBoxes,
    unit: 'box',
    note: tr(`Boxes of ${SCREWS_PER_BOX} · 1-5/8 in coarse thread for wood framing · ~${SCREWS_PER_SHEET} per sheet`, `Boîtes de ${SCREWS_PER_BOX} · filet grossier de 1 5/8 po pour ossature de bois · ~${SCREWS_PER_SHEET} par feuille`),
  })

  if (cornerBeadSticks > 0) {
    lines.push({
      item: 'metal corner bead drywall outside corner',
      label: tr('Corner bead', 'Baguette de coin'),
      kind: 'consumable',
      qty: cornerBeadSticks,
      unit: 'stick',
      note: tr('8-ft metal or vinyl corner bead for crisp outside corners', 'Baguette de coin en métal ou vinyle de 8 pi pour des coins extérieurs nets'),
    })
  } else {
    lines.push({
      item: 'metal corner bead drywall outside corner',
      label: tr('Corner bead', 'Baguette de coin'),
      kind: 'consumable',
      qty: 0,
      unit: 'stick',
      note: tr('Set the number of outside corners above to add these', 'Indiquez le nombre de coins extérieurs ci-dessus pour les ajouter'),
      optional: true,
    })
  }

  lines.push({
    item: 'drywall primer sealer interior',
    label: tr('Drywall primer', 'Apprêt à gypse'),
    kind: 'consumable',
    qty: Math.max(1, ceilTo(areaWithWaste / 400, 1)),
    unit: 'gal',
    note: tr('~400 sq ft per gallon · apply before painting to seal the joint compound', '~400 pi² par gallon · appliquer avant la peinture pour sceller le composé à joints'),
    optional: true,
  })

  lines.push({ item: 'drywall saw jab saw hand tool', label: tr('Drywall jab saw', 'Scie à guichet'), kind: 'tool', note: tr('For cutting outlets, switches and odd shapes.', 'Pour découper prises, interrupteurs et formes irrégulières.') })
  lines.push({ item: 'drywall t-square', label: tr('Drywall T-square', 'Équerre en T à gypse'), kind: 'tool', note: tr('Guides straight cuts across the full sheet width.', 'Guide les coupes droites sur toute la largeur de la feuille.') })
  lines.push({ item: 'drywall sanding sponge block', label: tr('Sanding sponge', 'Éponge à sabler'), kind: 'consumable', qty: Math.max(1, ceilTo(totalArea / 300, 1)), unit: 'sponge', note: tr('Medium grit for feathering compound seams.', 'Grain moyen pour estomper les joints de composé.') })
  lines.push({ item: 'drywall corner knife finishing trowel', label: tr('Finishing knives', 'Couteaux à joints'), kind: 'tool', note: tr('6 in + 10 in knives for taping and finishing coats.', 'Couteaux de 6 po + 10 po pour le rubanage et les couches de finition.'), optional: true })

  return {
    wallArea: round(wallArea, 0),
    ceilingArea: round(ceilingArea, 0),
    totalArea: round(totalArea, 0),
    areaWithWaste: round(areaWithWaste, 0),
    sheets,
    compoundBuckets,
    tapeRolls,
    screwBoxes,
    cornerBeadSticks,
    lines,
  }
}
